import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

// --- tree helpers ---

function flattenTree(nodes, depth = 0, parentPath = []) {
    const result = [];
    nodes.forEach((node, index) => {
        const path = [...parentPath, index];
        result.push({ node, depth, path });
        if (node.children?.length > 0) {
            result.push(...flattenTree(node.children, depth + 1, path));
        }
    });
    return result;
}

function pathKey(path) {
    return path.join('-');
}

function isDescendantOrSelf(path, ancestorPath) {
    if (path.length < ancestorPath.length) return false;
    return ancestorPath.every((v, i) => path[i] === v);
}

function pathsShareParent(a, b) {
    if (a.length !== b.length) return false;
    const parentA = a.slice(0, -1);
    const parentB = b.slice(0, -1);
    return parentA.length === parentB.length && parentA.every((v, i) => v === parentB[i]);
}

function moveNode(tree, fromPath, toPath) {
    const newTree = JSON.parse(JSON.stringify(tree));
    const parentPath = fromPath.slice(0, -1);
    let children = newTree;
    for (const idx of parentPath) {
        children = children[idx].children;
    }
    const fromIdx = fromPath[fromPath.length - 1];
    const toIdx = toPath[toPath.length - 1];
    if (fromIdx === toIdx) return newTree;
    const [item] = children.splice(fromIdx, 1);
    const adjustedTo = toIdx > fromIdx ? toIdx - 1 : toIdx;
    children.splice(adjustedTo, 0, item);
    return newTree;
}

let nodeCounter = 0;

function createNode(label) {
    return {
        id: `node_new_${Date.now()}_${++nodeCounter}`,
        order: 0,
        label,
        pictogram_id: 'placeholder',
        children: [],
    };
}

function addChildToNode(tree, parentPath, newNode) {
    const newTree = JSON.parse(JSON.stringify(tree));
    let node = newTree[parentPath[0]];
    for (let i = 1; i < parentPath.length; i++) {
        node = node.children[parentPath[i]];
    }
    node.children.push(newNode);
    return newTree;
}

function removeNode(tree, nodePath) {
    const newTree = JSON.parse(JSON.stringify(tree));
    const parentPath = nodePath.slice(0, -1);
    let children = newTree;
    for (const idx of parentPath) {
        children = children[idx].children;
    }
    children.splice(nodePath[nodePath.length - 1], 1);
    return newTree;
}

// --- component ---

export default function EditDialog({ open, onClose, tree, onSave, saving = false }) {
    const { t } = useTranslation();
    const [localTree, setLocalTree] = useState([]);
    const [dragPath, setDragPath] = useState(null);
    const [overPath, setOverPath] = useState(null);

    // Add-node state: afterPath (array = sibling after, 'root' = append to root), null = inactive
    const [addAfter, setAddAfter] = useState(null);
    const [newLabel, setNewLabel] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setLocalTree(JSON.parse(JSON.stringify(tree)));
            setAddAfter(null);
            setNewLabel('');
        }
    }, [open, tree]);

    useEffect(() => {
        if (addAfter !== null && inputRef.current) inputRef.current.focus();
    }, [addAfter]);

    const flatItems = useMemo(() => flattenTree(localTree), [localTree]);

    // --- drag & drop ---

    const handleDragStart = useCallback((path) => (e) => {
        setDragPath(path);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((path) => (e) => {
        e.preventDefault();
        if (dragPath && pathsShareParent(dragPath, path) && pathKey(dragPath) !== pathKey(path)) {
            e.dataTransfer.dropEffect = 'move';
            setOverPath(path);
        }
    }, [dragPath]);

    const handleDrop = useCallback((path) => (e) => {
        e.preventDefault();
        if (dragPath && pathsShareParent(dragPath, path)) {
            setLocalTree(prev => moveNode(prev, dragPath, path));
        }
        setDragPath(null);
        setOverPath(null);
    }, [dragPath]);

    const handleDragEnd = useCallback(() => {
        setDragPath(null);
        setOverPath(null);
    }, []);

    // --- delete node ---

    const handleDelete = (path) => {
        setLocalTree(prev => removeNode(prev, path));
    };

    // --- add node ---

    const startAdd = (afterPath) => {
        setAddAfter(afterPath);
        setNewLabel('');
    };

    const cancelAdd = () => {
        setAddAfter(null);
        setNewLabel('');
    };

    const confirmAdd = () => {
        const label = newLabel.trim();
        if (!label) return;
        const node = createNode(label);
        if (addAfter === 'root') {
            setLocalTree(prev => [...JSON.parse(JSON.stringify(prev)), node]);
        } else {
            setLocalTree(prev => addChildToNode(prev, addAfter, node));
        }
        setAddAfter(null);
        setNewLabel('');
    };

    const handleSave = () => {
        onSave(localTree);
        onClose();
    };

    // --- render helpers ---

    const addInputDepth = addAfter === 'root' ? 0 : addAfter ? addAfter.length : 0;

    const addInputRow = (
        <Box sx={{ display: 'flex', alignItems: 'center', pl: addInputDepth * 4 + 1, py: 0.5, pr: 1 }}>
            <TextField
                inputRef={inputRef}
                size="small"
                placeholder={t('APPLICATIONS.GRID.NEW_NODE_PLACEHOLDER')}
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmAdd();
                    if (e.key === 'Escape') cancelAdd();
                }}
                sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={confirmAdd} color="primary" disabled={!newLabel.trim()}>
                <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={cancelAdd}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('APPLICATIONS.GRID.EDIT_TITLE')}</DialogTitle>
            <DialogContent dividers>
                {flatItems.map(({ node, depth, path }, flatIdx) => {
                    const key = pathKey(path);
                    const isDragging = dragPath && pathKey(dragPath) === key;
                    const isOver = overPath && pathKey(overPath) === key && !isDragging;
                    const hasChildren = node.children?.length > 0;
                    // Show input after the last descendant of the add-target node
                    const isAddTarget = addAfter !== null && addAfter !== 'root' &&
                        isDescendantOrSelf(path, addAfter) &&
                        (flatIdx === flatItems.length - 1 || !isDescendantOrSelf(flatItems[flatIdx + 1].path, addAfter));
                    return (
                        <React.Fragment key={key}>
                            <Box
                                draggable
                                onDragStart={handleDragStart(path)}
                                onDragOver={handleDragOver(path)}
                                onDrop={handleDrop(path)}
                                onDragEnd={handleDragEnd}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: depth * 4 + 1,
                                    py: 1,
                                    pr: 1,
                                    borderRadius: 1,
                                    cursor: 'grab',
                                    opacity: isDragging ? 0.3 : 1,
                                    borderTop: isOver ? '2px solid' : '2px solid transparent',
                                    borderColor: isOver ? 'primary.main' : 'transparent',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                {hasChildren && <FolderIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />}
                                <Typography variant="body1" sx={{ flex: 1 }}>{node.label}</Typography>
                                {hasChildren && (
                                    <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>
                                        ({node.children.length})
                                    </Typography>
                                )}
                                <IconButton size="small" onClick={() => startAdd(path)} disabled={addAfter !== null}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(path)} disabled={addAfter !== null}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {isAddTarget && addInputRow}
                        </React.Fragment>
                    );
                })}

                {/* Add root-level node */}
                {addAfter === 'root' ? addInputRow : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => startAdd('root')}
                            disabled={addAfter !== null}
                        >
                            {t('APPLICATIONS.GRID.ADD_NODE')}
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('APPLICATIONS.GRID.CANCEL')}</Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? t('APPLICATIONS.GRID.SAVING') || 'Saving...' : t('APPLICATIONS.GRID.SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
